import { useEffect, useMemo, useState } from "react";
import { Layer, Source } from "react-map-gl";
import { useFilesForCompanyId } from "src/api/hooks/useFiles";
import { FileType, SasFileResult } from "src/types/InspectionUpdateRequest";
import axios from "axios";
import { GeoJSON } from "geojson";
import { mapSettings } from "../mapSettings";
import useMyUser from "src/api/hooks/useMyUser";
import { LocalStorageKeys } from "src/utils/localStorageKeys";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useCompletionsForNearestDay, useCompletionsForNearestWeek } from "src/api/hooks/useCompletionsForNearestWeek";
import { lang } from "src/language/Strings";
import useProjectVariations from "src/api/hooks/useProjectVariations";
import { useStore } from "src/components/Store";
// Generate a stable, pseudo-random color for each URL so the same URL
// always gets the same color, and different URLs are distinct.
function getColorForUrl(url: string): string {
  if (!url) {
    return "#888888";
  }

  // Simple string hash
  let hash = 0;
  for (let i = 0; i < url.length; i++) {
    hash = url.charCodeAt(i) + ((hash << 5) - hash);
    hash |= 0; // Convert to 32bit integer
  }

  // Map hash to a hue between 0 and 360
  const hue = Math.abs(hash) % 360;

  // Use fixed saturation/lightness so colors are vivid and distinct
  return `hsl(${hue}, 70%, 50%)`;
}

export function useMapFiles() {
  const { companyId } = useParams();
  const { data: files } = useFilesForCompanyId();
  const inspections = useProjectVariations();
  const { data: completions } = useCompletionsForNearestDay();
  const {data: myUser} = useMyUser();
  const groups = useMemo(() => {
    let selectedGroupIds = myUser?.selectedGroupIds ?? localStorage.getItem(LocalStorageKeys.selectedGroups) ?? "[]"
    if (selectedGroupIds){
      return JSON.parse(selectedGroupIds);
    }
    return [];
  }, [myUser?.id]);
  

  
  const geoJSONFiles = useMemo(() => {
    return files?.filter((file) => file.fileType === FileType.Map) ?? [];
  }, [files, groups]);

  return useQuery({
    queryKey: ["mapFiles", companyId, geoJSONFiles?.length, completions?.length],
    queryFn: async () => {
      if (geoJSONFiles && geoJSONFiles.length > 0) {
        const results: SasFileResult[] = [];
        for (const file of geoJSONFiles) {
          const fileUrl = file.url || file.files[0]!;
          const response = await axios.get(fileUrl);
          console.log("File:",file)
          const markerColorForFile = file.markerColor || file.action || getColorForUrl(fileUrl);

          const sasFileResult = {
            ...file,
            data: {...response.data, features: response.data.features.map((feature: any) => ({...feature, properties: {...feature.properties, featureType: "MapFileFeature",
              // Use a stable, URL-based color so each file has its own distinct marker color
              markerColor: markerColorForFile,
              markerBorder: inspections?.data?.some(i => i.externalId === feature.properties.OBJECTID) ? "#44dd00": "#ffffff",
              [lang.lastCompleted]: completions?.find(c => c.externalId === feature.properties.OBJECTID)?.weekDay
            }}))}
          } as SasFileResult & { data: any };
          results.push(sasFileResult);
        }
        return results;
      } else {
        return [];
      }
    }
  })
}

export function GeoJSONFiles() {
  const {data: completions} = useCompletionsForNearestWeek();
    const {data: mapFiles} = useMapFiles();
    const store = useStore();
    const processedFiles = useMemo(() => {
      if (!mapFiles) return [];
      
      return mapFiles.filter(file => store.displayedMapLayers.includes(file?.depiction)).map(file => ({
        ...file,
        data: {
          ...(file as any).data,
          features: (file as any).data.features.map((feature: any) => ({
            ...feature,
            properties: {
              ...feature.properties,
              featureType: "MapFileFeature",
              companyId: file.companyId,
              groupId: file.groupId,
              [lang.lastCompleted]: lang[completions?.find(c => c.externalId === feature.properties.OBJECTID)?.weekDay.toLowerCase() as keyof typeof lang]
            }
          }))
        }
      }));
    }, [mapFiles, completions, store.displayedMapLayers.length]);
    
    if (!mapFiles || processedFiles.length === 0) {
        return null;
    }
    
    
    return (
        <>
            {processedFiles.map((file) => (
                <Source 
                    key={file.id} 
                    id={`${mapSettings.uniqueSourcePrefix}-geojson-file-${file.id}`} 
                    type="geojson" 
                    data={(file as any).data as GeoJSON}
                >
                    {/* Points layer */}
                    <Layer
                        id={`${mapSettings.uniqueSourcePrefix}-geojson-file-${file.id}-points`}
                        type="circle"
                        filter={["==", ["geometry-type"], "Point"]}
                        paint={{
                            // Slightly smaller, zoom‑based radius so dense
                            // areas don't turn into a single blob, while
                            // still growing at higher zoom levels.
                            "circle-radius": [
                                "interpolate",
                                ["linear"],
                                ["zoom"],
                                4, 2,
                                8, 3,
                                12, 5,
                                15, 8,
                                18, 12
                            ],
                            "circle-color": ["get", "markerColor"],
                            // Add a subtle white stroke so nearby points
                            // keep their individual shape.
                            "circle-stroke-width": 1.5,
                            "circle-stroke-color": ["get","markerBorder"],
                            "circle-opacity": 0.8,
                        }}
                    />
                    {/* Polygon fill layer */}
                    <Layer
                        id={`${mapSettings.uniqueSourcePrefix}-geojson-file-${file.id}-fill`}
                        type="fill"
                        filter={["in", ["geometry-type"], ["literal", ["Polygon", "MultiPolygon"]]]}
                        paint={{
                            "fill-color": "#0080ff",
                            "fill-opacity": 0.5,
                        }}
                    />
                    {/* Polygon stroke layer */}
                    <Layer
                        id={`${mapSettings.uniqueSourcePrefix}-geojson-file-${file.id}-stroke`}
                        type="line"
                        filter={["in", ["geometry-type"], ["literal", ["Polygon", "MultiPolygon"]]]}
                        paint={{
                            "line-color": "#0066cc",
                            "line-width": 2,
                            "line-opacity": 0.8,
                        }}
                    />
                    {/* LineString layer */}
                    <Layer
                        id={`${mapSettings.uniqueSourcePrefix}-geojson-file-${file.id}-lines`}
                        type="line"
                        filter={["in", ["geometry-type"], ["literal", ["LineString", "MultiLineString"]]]}
                        paint={{
                            "line-color": "#0066cc",
                            "line-width": 2,
                            "line-opacity": 0.8,
                        }}
                    />
                </Source>
            ))}
        </>
    );
}